import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Button,
  Badge,
  Dropdown,
  Avatar,
  Space,
  Popover,
  List,
  Typography,
  Empty,
  Divider,
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  CalendarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { logout } from '@/store/slices/authSlice';
import { toggleSidebar, markNotificationRead, clearNotifications } from '@/store/slices/uiSlice';

dayjs.extend(relativeTime);

const { Header: AntHeader } = Layout;
const { Text } = Typography;

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { admin } = useAppSelector((state) => state.auth);
  const { sidebarCollapsed, notifications } = useAppSelector((state) => state.ui);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const unreadNotifications = notifications.filter((n) => !n.read);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleNotificationClick = (id: string) => {
    dispatch(markNotificationRead(id));
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/administrators/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  const notificationContent = (
    <div style={{ width: 380 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #f3f4f6',
          background: '#fafbfc',
        }}
      >
        <div>
          <Text strong style={{ fontSize: 15, display: 'block' }}>Notifications</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {unreadNotifications.length} unread
          </Text>
        </div>
        {notifications.length > 0 && (
          <Button
            type="text"
            size="small"
            onClick={() => dispatch(clearNotifications())}
            style={{ color: '#2563eb' }}
          >
            Mark all read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications yet"
          style={{ padding: '40px 0' }}
        />
      ) : (
        <List
          dataSource={notifications.slice(0, 5)}
          style={{ maxHeight: 420, overflow: 'auto' }}
          renderItem={(item) => (
            <List.Item
              onClick={() => handleNotificationClick(item.id)}
              style={{
                padding: '14px 20px',
                cursor: 'pointer',
                background: item.read ? '#ffffff' : '#f8fafc',
                borderLeft: item.read ? 'none' : '3px solid #2563eb',
                transition: 'all 0.2s ease',
              }}
              className="hover:bg-gray-50"
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background:
                        item.type === 'success'
                          ? '#ecfdf5'
                          : item.type === 'error'
                          ? '#fef2f2'
                          : item.type === 'warning'
                          ? '#fffbeb'
                          : '#eff6ff',
                    }}
                  >
                    <Badge
                      status={
                        item.type === 'success'
                          ? 'success'
                          : item.type === 'error'
                          ? 'error'
                          : item.type === 'warning'
                          ? 'warning'
                          : 'processing'
                      }
                    />
                  </div>
                }
                title={
                  <Text
                    style={{
                      fontWeight: item.read ? 400 : 600,
                      fontSize: 13,
                      color: '#1f2937',
                    }}
                  >
                    {item.message}
                  </Text>
                }
                description={
                  <Space direction="vertical" size={2}>
                    {item.description && (
                      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                        {item.description}
                      </Text>
                    )}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(item.timestamp).fromNow()}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}

      {notifications.length > 5 && (
        <>
          <Divider style={{ margin: 0 }} />
          <div style={{ padding: '14px 20px', textAlign: 'center', background: '#fafbfc' }}>
            <Button
              type="link"
              onClick={() => navigate('/notifications')}
              style={{ fontWeight: 500 }}
            >
              View All Notifications
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <AntHeader
      className="premium-header"
      style={{
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 99,
        height: 64,
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* Left Side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Sidebar Toggle */}
        <Button
          type="text"
          icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => dispatch(toggleSidebar())}
          style={{
            fontSize: 16,
            width: 36,
            height: 36,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
          }}
        />

        {/* Divider */}
        <div style={{ width: 1, height: 24, background: '#e5e7eb' }} />

        {/* Date Display */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarOutlined style={{ fontSize: 14, color: '#9ca3af' }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>
            {dayjs().format('ddd, MMM D, YYYY')}
          </span>
        </div>
      </div>

      {/* Right Side */}
      <Space size={8}>
        {/* Search Button */}
        <Button
          type="text"
          icon={<SearchOutlined />}
          style={{
            fontSize: 16,
            width: 40,
            height: 40,
            borderRadius: 10,
          }}
          title="Search"
        />

        {/* Fullscreen Toggle */}
        <Button
          type="text"
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={toggleFullscreen}
          style={{
            fontSize: 16,
            width: 40,
            height: 40,
            borderRadius: 10,
          }}
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        />

        {/* Notifications */}
        <Popover
          content={notificationContent}
          trigger="click"
          placement="bottomRight"
          arrow={{ pointAtCenter: true }}
          overlayStyle={{ padding: 0 }}
          overlayInnerStyle={{ padding: 0, borderRadius: 12, overflow: 'hidden' }}
        >
          <Badge count={unreadNotifications.length} size="small" offset={[-4, 4]}>
            <Button
              type="text"
              icon={<BellOutlined />}
              style={{
                fontSize: 16,
                width: 40,
                height: 40,
                borderRadius: 10,
              }}
            />
          </Badge>
        </Popover>

        {/* Divider */}
        <div
          style={{
            width: 1,
            height: 24,
            background: '#e5e7eb',
            margin: '0 8px',
          }}
        />

        {/* User Dropdown */}
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
          <div className="user-dropdown-trigger">
            <Avatar
              size={38}
              icon={<UserOutlined />}
              src={admin?.avatar}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
              }}
            />
            <div style={{ lineHeight: 1.3 }}>
              <Text strong style={{ display: 'block', fontSize: 13, color: '#1f2937' }}>
                {admin?.name || 'Admin User'}
              </Text>
              <Text style={{ fontSize: 11, color: '#6b7280' }}>
                {admin?.role || 'Administrator'}
              </Text>
            </div>
          </div>
        </Dropdown>
      </Space>
    </AntHeader>
  );
};

export default Header;
